import { useCallback, useEffect, useRef, useState } from 'react';
import { readStorage, writeStorage } from '@/lib/localStorage';

/**
 * Reactive localStorage hook with stable setter reference and cross-tab sync
 * via the `storage` event.
 */
export function useLocalStorage<T>(
  key: string,
  fallback: T,
): [T, (value: T | ((prev: T) => T)) => void] {
  const [value, setValueState] = useState<T>(() => readStorage<T>(key, fallback));

  // Keep a ref for the latest value so the setter stays stable.
  const valueRef = useRef(value);
  valueRef.current = value;

  const setValue = useCallback(
    (next: T | ((prev: T) => T)) => {
      const resolved =
        typeof next === 'function' ? (next as (prev: T) => T)(valueRef.current) : next;
      valueRef.current = resolved;
      setValueState(resolved);
      writeStorage(key, resolved);
    },
    [key],
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onStorage = (e: StorageEvent) => {
      if (e.key !== key) return;
      if (e.newValue === null) {
        setValueState(fallback);
        return;
      }
      try {
        setValueState(JSON.parse(e.newValue) as T);
      } catch {
        /* drop malformed cross-tab payload */
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [key, fallback]);

  return [value, setValue];
}
